import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { Button, Modal, Space, Table, Tag, Tooltip, message, Tabs } from 'antd';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import TransactionService from '../../service/transaction-service/Transaction.service';
import useEmployee from '../../hook/useEmployee';

const { TabPane } = Tabs;

// Interface cho transaction trong sub-table
interface TransactionData {
  id: number;
  transactionDate: string;
  transactionType: string;
  amount: number;
  feeApplied?: number;
  balance: number;
  status: string;
  employee: {
    id: number;
    name: string;
  };
  posDevice?: {
    id: number;
    deviceCode: string;
  };
  deletedByName?: string;
}

interface TransactionSubTableProps {
  maturityScheduleId: number;
  onTransactionUpdate?: () => void;
  refreshTrigger?: number; // Add this to force refresh when value changes
  isAdmin?: boolean; // Chỉ admin mới thấy nút "Đánh dấu hoàn thành" giao dịch
}

// CSS styles for the sub-table
const subTableStyles = `
  .transaction-sub-table .ant-table-thead > tr > th {
    background-color: #f8fafc !important;
    font-size: 12px;
    padding: 8px 12px !important;
    border-bottom: 1px solid #e2e8f0;
    font-weight: 600;
  }
  
  .transaction-sub-table .ant-table-tbody > tr > td {
    padding: 6px 12px !important;
    font-size: 12px;
    border-bottom: 1px solid #f1f5f9;
  }
  
  .transaction-sub-table .ant-table-tbody > tr:hover > td {
    background-color: #f1f5f9 !important;
  }
  
  .ant-table-expanded-row-fixed {
    margin: 0 !important;
  }
  
  .transaction-sub-table .ant-table-container {
    border: 1px solid #e2e8f0;
    border-radius: 6px;
  }
  
  .transaction-sub-table .ant-table-tbody > tr:last-child > td {
    border-bottom: none;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = subTableStyles;
  if (!document.head.querySelector('style[data-transaction-sub-table]')) {
    styleElement.setAttribute('data-transaction-sub-table', 'true');
    document.head.appendChild(styleElement);
  }
}

const TransactionSubTable: React.FC<TransactionSubTableProps> = ({
  maturityScheduleId,
  onTransactionUpdate,
  refreshTrigger,
  isAdmin = false,
}) => {
  const { employee } = useEmployee();
  const [transactions, setTransactions] = useState<TransactionData[]>([]);
  const [deletedTransactions, setDeletedTransactions] = useState<
    TransactionData[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [deletedLoading, setDeletedLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletedError, setDeletedError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('active');
  const [deletedDataLoaded, setDeletedDataLoaded] = useState(false);

  // Load transactions for this maturity schedule
  useEffect(() => {
    const loadTransactions = async () => {
      setLoading(true);
      setError(null);
      try {
        const response =
          await TransactionService.getTransactionsByMaturityScheduleWithEmployee(
            maturityScheduleId
          );
        if (response.success && response.data) {
          // Transform API response to match our interface
          const transformedTransactions = response.data.map(
            (transaction: any) => ({
              id: transaction.id,
              transactionDate: transaction.transactionDate,
              transactionType: transaction.transactionType,
              amount: transaction.amount,
              feeApplied: transaction.feeApplied,
              balance: transaction.balance,
              status: transaction.status,
              employee: {
                id: transaction.employee?.id || 0,
                name: transaction.employee?.name || 'N/A',
              },
              posDevice: transaction.posDevice
                ? {
                  id: transaction.posDevice.id,
                  deviceCode: transaction.posDevice.deviceCode,
                }
                : undefined,
            })
          );
          setTransactions(transformedTransactions);
        } else {
          setError(response.message || 'Không thể tải danh sách giao dịch');
        }
      } catch (error) {
        console.error('Error loading transactions:', error);
        setError('Có lỗi xảy ra khi tải danh sách giao dịch');
        setTransactions([]);
      } finally {
        setLoading(false);
      }
    };

    loadTransactions();
  }, [maturityScheduleId, refreshTrigger]); // Add refreshTrigger to dependencies

  // Load deleted transactions (only when needed)
  const loadDeletedTransactions = async () => {
    if (deletedDataLoaded && !refreshTrigger) return; // Avoid redundant calls

    setDeletedLoading(true);
    setDeletedError(null);
    try {
      const response =
        await TransactionService.getDeletedTransactionsByMaturityScheduleWithEmployee(
          maturityScheduleId
        );
      if (response.success && response.data) {
        const transformedTransactions = response.data.map(
          (transaction: any) => ({
            id: transaction.id,
            transactionDate: transaction.transactionDate,
            transactionType: transaction.transactionType,
            amount: transaction.amount,
            feeApplied: transaction.feeApplied,
            balance: transaction.balance,
            status: transaction.status,
            employee: {
              id: transaction.employee?.id || 0,
              name: transaction.employee?.name || 'N/A',
            },
            posDevice: transaction.posDevice
              ? {
                id: transaction.posDevice.id,
                deviceCode: transaction.posDevice.deviceCode,
              }
              : undefined,
            deletedByName: transaction.deletedByName,
          })
        );
        setDeletedTransactions(transformedTransactions);
        setDeletedDataLoaded(true);
      } else {
        setDeletedError(
          response.message || 'Không thể tải danh sách giao dịch đã xóa'
        );
      }
    } catch (error) {
      console.error('Error loading deleted transactions:', error);
      setDeletedError('Có lỗi xảy ra khi tải danh sách giao dịch đã xóa');
      setDeletedTransactions([]);
    } finally {
      setDeletedLoading(false);
    }
  };

  // Handle tab change
  const handleTabChange = (key: string) => {
    setActiveTab(key);
    if (key === 'deleted' && !deletedDataLoaded) {
      loadDeletedTransactions();
    }
  };

  // Refresh deleted transactions when refreshTrigger changes and tab is active
  useEffect(() => {
    if (refreshTrigger && activeTab === 'deleted' && deletedDataLoaded) {
      loadDeletedTransactions();
    }
  }, [refreshTrigger, activeTab, deletedDataLoaded]);

  // Refresh function to reload transactions
  const refreshTransactions = () => {
    const loadTransactions = async () => {
      setLoading(true);
      setError(null);
      try {
        const response =
          await TransactionService.getTransactionsByMaturityScheduleWithEmployee(
            maturityScheduleId
          );
        if (response.success && response.data) {
          const transformedTransactions = response.data.map(
            (transaction: any) => ({
              id: transaction.id,
              transactionDate: transaction.transactionDate,
              transactionType: transaction.transactionType,
              amount: transaction.amount,
              feeApplied: transaction.feeApplied,
              balance: transaction.balance,
              status: transaction.status,
              employee: {
                id: transaction.employee?.id || 0,
                name: transaction.employee?.name || 'N/A',
              },
              posDevice: transaction.posDevice
                ? {
                  id: transaction.posDevice.id,
                  deviceCode: transaction.posDevice.deviceCode,
                }
                : undefined,
            })
          );
          setTransactions(transformedTransactions);
          // Call parent refresh if needed
          if (onTransactionUpdate) {
            onTransactionUpdate();
          }
        } else {
          setError(response.message || 'Không thể tải danh sách giao dịch');
        }
      } catch (error) {
        console.error('Error loading transactions:', error);
        setError('Có lỗi xảy ra khi tải danh sách giao dịch');
        setTransactions([]);
      } finally {
        setLoading(false);
      }
    };

    loadTransactions();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
    }).format(amount);
  };

  // Handle delete transaction
  const handleDeleteTransaction = async (transactionId: number) => {
    // Find the transaction to get its status
    const transaction = transactions.find((t) => t.id === transactionId);
    const isCompleted = transaction?.status === 'COMPLETED';

    Modal.confirm({
      title: 'Xác nhận xóa giao dịch',
      content: isCompleted
        ? 'Bạn có chắc chắn muốn xóa giao dịch này? Giao dịch đã hoàn thành sẽ được hoàn tác số dư. Hành động này không thể hoàn tác.'
        : 'Bạn có chắc chắn muốn xóa giao dịch này? Hành động này không thể hoàn tác.',
      okText: 'Xóa',
      okType: 'danger',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          setLoading(true);
          const response =
            await TransactionService.softDeleteTransaction(transactionId, employee?.id as number);
          if (response.success) {
            message.success(
              isCompleted
                ? 'Xóa giao dịch thành công và đã hoàn tác số dư'
                : 'Xóa giao dịch thành công'
            );
            // Refresh active transactions list
            refreshTransactions();
            // If deleted tab was loaded, refresh it too
            if (deletedDataLoaded) {
              loadDeletedTransactions();
            }
          } else {
            setError(response.message || 'Không thể xóa giao dịch');
          }
        } catch (error) {
          console.error('Error deleting transaction:', error);
          setError('Có lỗi xảy ra khi xóa giao dịch');
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // Handle toggle withdrawal status
  const handleToggleStatus = async (transactionId: number) => {
    try {
      setLoading(true);
      const response =
        await TransactionService.toggleWithdrawalStatus(transactionId);
      if (response.success) {
        const newStatus = response.data.status;
        message.success(
          `Cập nhật trạng thái thành công: ${newStatus === 'COMPLETED' ? 'Hoàn thành' : 'Chưa hoàn thành'}`
        );
        // Refresh transactions list
        refreshTransactions();
      } else {
        setError(response.message || 'Không thể cập nhật trạng thái giao dịch');
      }
    } catch (error) {
      console.error('Error toggling transaction status:', error);
      setError('Có lỗi xảy ra khi cập nhật trạng thái giao dịch');
    } finally {
      setLoading(false);
    }
  };

  const transactionColumns: ColumnsType<TransactionData> = [
    {
      title: 'Ngày giao dịch',
      dataIndex: 'transactionDate',
      key: 'transactionDate',
      width: 120,
      render: (date: string) => dayjs(date).format('DD/MM/YYYY'),
    },
    {
      title: 'Loại',
      dataIndex: 'transactionType',
      key: 'transactionType',
      width: 80,
      render: (type: string) => (
        <Tag color={type === 'deposit' ? 'green' : 'blue'}>
          {type === 'deposit' ? 'Nạp' : 'Rút'}
        </Tag>
      ),
    },
    {
      title: 'Số tiền',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      render: (amount: number) => (
        <span className="font-semibold">{formatCurrency(amount)}</span>
      ),
    },
    {
      title: 'Phí POS (%)',
      dataIndex: 'feeApplied',
      key: 'feeApplied',
      width: 80,
      render: (fee?: number) => (fee ? `${fee}%` : '-'),
    },
    {
      title: 'POS',
      key: 'posDevice',
      width: 80,
      render: (_, record) => record.posDevice?.deviceCode || '-',
    },
    {
      title: 'Thực nhận',
      dataIndex: 'balance',
      key: 'balance',
      width: 120,
      render: (balance: number) => (
        <span className="text-blue-600 font-semibold">
          {formatCurrency(balance)}
        </span>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => (
        <Tag color={status === 'COMPLETED' ? 'green' : 'orange'}>
          {status === 'COMPLETED' ? 'Hoàn thành' : 'Đang chờ'}
        </Tag>
      ),
    },
    {
      title: 'Nhân viên',
      key: 'employee',
      width: 100,
      render: (_, record) => record.employee?.name || '-',
    },
    {
      title: 'Hành động',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          {/* Nút xóa giao dịch - cho tất cả giao dịch */}
          <Tooltip title="Xóa giao dịch">
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteTransaction(record.id)}
            />
          </Tooltip>

          {/* Nút toggle trạng thái - chỉ cho withdrawal, chỉ admin */}
          {isAdmin && record.transactionType === 'withdrawal' && (
            <Tooltip
              title={
                record.status === 'COMPLETED'
                  ? 'Chuyển về chưa hoàn thành'
                  : 'Đánh dấu hoàn thành'
              }
            >
              <Button
                type={record.status === 'COMPLETED' ? 'default' : 'primary'}
                size="small"
                icon={
                  record.status === 'COMPLETED' ? (
                    <CloseOutlined />
                  ) : (
                    <CheckOutlined />
                  )
                }
                style={{
                  backgroundColor:
                    record.status === 'COMPLETED' ? '#fff2f0' : '#f6ffed',
                  borderColor:
                    record.status === 'COMPLETED' ? '#ffccc7' : '#b7eb8f',
                  color: record.status === 'COMPLETED' ? '#ff4d4f' : '#52c41a',
                }}
                onClick={() => handleToggleStatus(record.id)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  // Columns for deleted transactions (no actions)
  const deletedTransactionColumns: ColumnsType<TransactionData> = [
    {
      title: 'Ngày giao dịch',
      dataIndex: 'transactionDate',
      key: 'transactionDate',
      width: 120,
      render: (date: string) => dayjs(date).format('DD/MM/YYYY'),
    },
    {
      title: 'Loại',
      dataIndex: 'transactionType',
      key: 'transactionType',
      width: 80,
      render: (type: string) => (
        <Tag color={type === 'deposit' ? 'green' : 'blue'}>
          {type === 'deposit' ? 'Nạp' : 'Rút'}
        </Tag>
      ),
    },
    {
      title: 'Số tiền',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      render: (amount: number) => (
        <span className="font-semibold">{formatCurrency(amount)}</span>
      ),
    },
    {
      title: 'Phí POS (%)',
      dataIndex: 'feeApplied',
      key: 'feeApplied',
      width: 80,
      render: (fee?: number) => (fee ? `${fee}%` : '-'),
    },
    {
      title: 'POS',
      key: 'posDevice',
      width: 80,
      render: (_, record) => record.posDevice?.deviceCode || '-',
    },
    {
      title: 'Thực nhận',
      dataIndex: 'balance',
      key: 'balance',
      width: 120,
      render: (balance: number) => (
        <span className="text-blue-600 font-semibold">
          {formatCurrency(balance)}
        </span>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => (
        <Tag color={status === 'COMPLETED' ? 'green' : 'orange'}>
          {status === 'COMPLETED' ? 'Hoàn thành' : 'Đang chờ'} (Đã xóa)
        </Tag>
      ),
    },
    {
      title: 'Nhân viên',
      key: 'employee',
      width: 100,
      render: (_, record) => record.employee?.name || '-',
    },
    {
      title: 'Người xóa',
      dataIndex: 'deletedByName',
      key: 'deletedByName',
      width: 120,
      render: (deletedByName: string | null) => deletedByName || '-',
    },
  ];

  return (
    <div className="bg-gray-50 p-4 rounded">
      <div className="flex justify-between items-center mb-3">
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          className="flex-1"
        >
          <TabPane tab="Giao dịch hiện tại" key="active" />
          <TabPane tab="Giao dịch đã xóa" key="deleted" />
        </Tabs>
        <h4 className="text-sm font-semibold text-gray-700 ml-4">
          Lịch sử giao dịch (Kỳ đáo hạn #{maturityScheduleId})
        </h4>
      </div>

      <div>
        {activeTab === 'active' && (
          <>
            <div className="mb-3 flex justify-end">
              <Button
                type="text"
                size="small"
                onClick={refreshTransactions}
                loading={loading}
                className="text-blue-600 hover:text-blue-800"
              >
                Làm mới
              </Button>
            </div>

            {error && (
              <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
                <div className="font-medium">Lỗi tải dữ liệu:</div>
                <div>{error}</div>
              </div>
            )}

            <Table
              columns={transactionColumns}
              dataSource={transactions}
              rowKey="id"
              size="small"
              loading={loading}
              pagination={false}
              className="transaction-sub-table"
              scroll={{ x: 860 }}
              locale={{
                emptyText: !error ? (
                  <div className="text-center py-8">
                    <div className="text-gray-400 mb-2">
                      Chưa có giao dịch nào
                    </div>
                    <div className="text-xs text-gray-500">
                      Các giao dịch nạp/rút tiền sẽ hiển thị tại đây
                    </div>
                  </div>
                ) : null,
              }}
            />
          </>
        )}

        {activeTab === 'deleted' && (
          <>
            <div className="mb-3 flex justify-end">
              <Button
                type="text"
                size="small"
                onClick={loadDeletedTransactions}
                loading={deletedLoading}
                className="text-blue-600 hover:text-blue-800"
              >
                Làm mới
              </Button>
            </div>

            {deletedError && (
              <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
                <div className="font-medium">Lỗi tải dữ liệu:</div>
                <div>{deletedError}</div>
              </div>
            )}

            <Table
              columns={deletedTransactionColumns}
              dataSource={deletedTransactions}
              rowKey="id"
              size="small"
              loading={deletedLoading}
              pagination={false}
              className="transaction-sub-table"
              scroll={{ x: 860 }}
              locale={{
                emptyText: !deletedError ? (
                  <div className="text-center py-8">
                    <div className="text-gray-400 mb-2">
                      Không có giao dịch đã xóa
                    </div>
                    <div className="text-xs text-gray-500">
                      Các giao dịch đã xóa sẽ hiển thị tại đây
                    </div>
                  </div>
                ) : null,
              }}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default TransactionSubTable;
